import * as React from "react";

import { useAnalytics, useAuth } from "@/providers";
import { CustomFieldValuesSection } from "@/shared/components/CustomFields";
import { TargetEntityType } from "@/shared/domain/customFields";
import { DatasetRecord } from "@/shared/domain/datasets";
import { useCreateDataset } from "@/shared/domain/datasets/hooks";
import {
  Banner,
  Field,
  Fieldset,
  RobotoButton,
  TextInput,
} from "@/shared/foundation/primitives";
import { RobotoDomainException } from "@/shared/http";

import { MetadataForm, MetadataView } from "../Metadata";
import { AutocompleteType } from "../TagAndMetadataAutocomplete";
import { TagsEditor } from "../TagsEditor";

import styles from "./CreateDatasetForm.module.css";

interface CreateDatasetFormProps {
  onSuccess?: (dataset: DatasetRecord) => void;
}

/**
 * The New Dataset form: a name, tags, metadata and, when the org has them,
 * custom fields, all optional. Shown in the New Dataset popover and on the
 * Create Dataset page.
 */
export function CreateDatasetForm({ onSuccess }: CreateDatasetFormProps) {
  const [tags, setTags] = React.useState<string[]>([]);
  const [name, setName] = React.useState<string>("");
  const [metadata, setMetadata] = React.useState<Record<string, unknown>>({});
  const [customFieldValues, setCustomFieldValues] = React.useState<
    Record<string, unknown>
  >({});
  const { currentOrganization } = useAuth();
  const { trackEvent } = useAnalytics();

  const createDatasetMutation = useCreateDataset();

  const createDataset = () => {
    if (!currentOrganization?.org_id) {
      return;
    }

    const isNameEmpty = name.trim() === "";

    createDatasetMutation.mutate(
      {
        request: {
          tags: tags,
          metadata: metadata,
          name: isNameEmpty ? undefined : name,
          custom_fields:
            Object.keys(customFieldValues).length > 0
              ? customFieldValues
              : undefined,
        },
        resourceOwnerId: currentOrganization.org_id,
      },
      {
        onSuccess,
      },
    );
  };

  return (
    <div className={styles.form}>
      {/* Every field is optional, so it's said once here rather than on
          each label. */}
      <p className={styles.note}>All fields are optional.</p>

      <Field.Root className={styles.section}>
        <Field.Label>Name</Field.Label>
        <Field.Description>
          Add a name for this dataset. Up to 120 characters.
        </Field.Description>
        <Field.Control
          render={<TextInput />}
          placeholder="Enter a name"
          maxLength={120}
          value={name}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setName(event.target.value);
          }}
        />
      </Field.Root>

      <Fieldset.Root className={styles.section}>
        <Fieldset.Legend>Tags</Fieldset.Legend>
        <Fieldset.Description>
          Add tags so it&apos;s easier to find later.
        </Fieldset.Description>
        <TagsEditor
          tags={tags}
          mode="controlled"
          variant="field"
          autocompleteType={AutocompleteType.DatasetTags}
          onAdd={(tag) => setTags((prev) => [...prev, tag])}
          onRemove={(tag) => setTags((prev) => prev.filter((t) => t !== tag))}
        />
      </Fieldset.Root>

      <Fieldset.Root className={styles.section}>
        <Fieldset.Legend>Metadata</Fieldset.Legend>
        <Fieldset.Description>
          Key-value pairs, e.g. softwareVersion = 1.0.3.
        </Fieldset.Description>
        <MetadataView metadata={metadata} onChange={setMetadata} />
        <MetadataForm
          metadata={metadata}
          autocompleteType={AutocompleteType.DatasetMetadataKeys}
          onAddMetadata={(key, value) => {
            setMetadata({
              ...metadata,
              [key]: value,
            });
          }}
        />
      </Fieldset.Root>

      {currentOrganization?.org_id && (
        // The section renders nothing when the org has no custom fields,
        // and the rule above it goes with it.
        <CustomFieldValuesSection
          className={styles.section}
          entityType={TargetEntityType.Dataset}
          orgId={currentOrganization.org_id}
          values={customFieldValues}
          onChange={setCustomFieldValues}
          header={
            <div className={styles.heading}>
              <span className={styles.label}>Custom Fields</span>
              <p className={styles.description}>
                Set values for this dataset&apos;s custom fields.
              </p>
            </div>
          }
        />
      )}

      {createDatasetMutation.isError && (
        <Banner
          variant="error"
          role="alert"
          text={formatSubmitError(createDatasetMutation.error)}
          onDismiss={() => {
            createDatasetMutation.reset();
          }}
        />
      )}

      <div className={styles.actions}>
        <RobotoButton
          variant="primary"
          onClick={() => {
            trackEvent("CreateDatasetFormSubmitted", {
              source: "CreateDatasetPopover",
            });
            createDataset();
          }}
          disabled={createDatasetMutation.isPending}
          loading={createDatasetMutation.isPending}
        >
          Create dataset
        </RobotoButton>
      </div>
    </div>
  );
}

function formatSubmitError(err: unknown): string {
  if (err instanceof RobotoDomainException && err.isClientError()) {
    return err.message;
  }
  return "Failed to create dataset. Try again or reach out to support@roboto.ai if problem persists.";
}
